import FinalStateNode from "./FinalStateNode";
import { useSelector, useDispatch } from 'react-redux';
import { Box, Stack, IconButton, Tooltip, Modal, Sheet, Button, Input } from "@mui/joy";
import { ArrowForward, Add } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { API_PATH } from '../Internal/api.js';
import { extractIsobars } from '../Internal/helpers.js';
import PDGSearchbar from './PDGSearchbar';
import SpinSelector from './SpinSelector';
import ParitySelector from './ParitySelector';

export default function FinalStateSelector() {
    const finalState = useSelector(state => state.finalState);
    const n = useSelector(state => state.decay);
    const locked = useSelector(state => state.decayLock);
    const dispatch = useDispatch();

    const [addOpen, setAddOpen] = useState(false);
    const [newSpin, setNewSpin] = useState(0);
    const [newParity, setNewParity] = useState(1);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        if (typeof n === 'number') return;
        const defaultN = 3;
        const defaultNodes = [1, 2, 3];
        dispatch({ type: 'SET_DECAY', payload: defaultN });
        dispatch({ type: 'SET_FINAL_STATE', payload: defaultNodes });
        fetch(API_PATH + "topologies?n=" + defaultN)
            .then(r => r.json())
            .then(data => {
                const isobars = {};
                data.topologies.forEach(topology => {
                    extractIsobars(topology[0], isobars);
                    extractIsobars(topology[1], isobars);
                });
                dispatch({ type: 'SET_ISOBAR', payload: isobars });
                dispatch({ type: 'SET_GRAPH', payload: data.topologies });
            })
            .catch(console.error);
    }, []);

    if (!finalState.nodes) return <div />;

    const applyNewDecay = (newN, newNodes) => {
        dispatch({ type: 'SET_DECAY', payload: newN });
        dispatch({ type: 'SET_MAIN_GRAPH', payload: null });
        dispatch({ type: 'SET_TREE_MASK', payload: {} });
        dispatch({ type: 'SET_GRAPH_CHECK', payload: {} });
        dispatch({ type: 'SET_FINAL_STATE', payload: newNodes });
        fetch(API_PATH + "topologies?n=" + newN)
            .then(r => r.json())
            .then(data => {
                const isobars = {};
                data.topologies.forEach(topology => {
                    extractIsobars(topology[0], isobars);
                    extractIsobars(topology[1], isobars);
                });
                dispatch({ type: 'SET_ISOBAR', payload: isobars });
                dispatch({ type: 'SET_GRAPH', payload: data.topologies });
            })
            .catch(console.error);
    };

    const handleAdd = () => {
        const newNode = n + 1;
        const updatedData = {
            ...finalState.finalStateData,
            [newNode]: { spin: newSpin, parity: newParity, name: newName || `P${newNode}` },
        };
        dispatch({ type: 'SET_FINAL_STATE_DATA', payload: updatedData });
        applyNewDecay(newNode, [...finalState.nodes, newNode]);
        setAddOpen(false);
        setNewSpin(0);
        setNewParity(1);
        setNewName('');
    };

    const handleDelete = (nodeToDelete) => {
        if (n <= 3) return;
        const remaining = finalState.nodes.filter(nd => nd !== nodeToDelete);
        const newData = { 0: finalState.finalStateData[0] };
        remaining.forEach((oldNode, i) => {
            newData[i + 1] = finalState.finalStateData[oldNode];
        });
        dispatch({ type: 'SET_FINAL_STATE_DATA', payload: newData });
        applyNewDecay(n - 1, remaining.map((_, i) => i + 1));
    };

    return (
        <div>
            <Modal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Sheet variant="outlined" sx={{ maxWidth: 500, borderRadius: 'md', p: 3, boxShadow: 'lg' }}>
                    <Stack spacing={1}>
                        <SpinSelector value={newSpin} onChange={(_, v) => setNewSpin(v)} toolTip="Spin of new particle" />
                        <ParitySelector value={newParity} onChange={(_, v) => setNewParity(v)} toolTip="Parity of new particle" />
                        <Input
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            placeholder={`P${n + 1}`}
                        />
                        <PDGSearchbar
                            onParticleSelect={p => {
                                if (p) {
                                    setNewName(p.label);
                                    setNewSpin(p.spin);
                                    setNewParity(p.parity || 1);
                                }
                            }}
                        />
                        <Button onClick={handleAdd}>Add Particle</Button>
                    </Stack>
                </Sheet>
            </Modal>

            <Stack spacing={2} direction="row" alignItems="center">
                <FinalStateNode key={0} node={0} decaying={true} />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowForward />
                </Box>
                <Stack spacing={2} direction="row" alignItems="center">
                    {finalState.nodes.map(nd => (
                        <FinalStateNode
                            key={nd}
                            node={nd}
                            onDelete={() => handleDelete(nd)}
                            deleteDisabled={n <= 3}
                        />
                    ))}

                    {/* Add particle node */}
                    <Tooltip title="Add particle">
                        <IconButton
                            onClick={() => setAddOpen(true)}
                            disabled={locked}
                            sx={{
                                width: '4rem',
                                height: '4rem',
                                borderRadius: '50%',
                                border: '2px dashed',
                                borderColor: 'neutral.400',
                                color: 'neutral.400',
                                opacity: 0.6,
                                '&:hover': { opacity: 1 },
                            }}
                        >
                            <Add />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
        </div>
    );
}
